import { ForwardRefExoticComponent, MouseEvent, RefAttributes, forwardRef, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useTheme } from '@mui/styles';
import useConfig from 'hooks/useConfig';
import { useDispatch, useSelector } from 'store';
import { activeItem, activeMenuItem, cleanOpenedAction, openDrawer } from 'store/slices/menu';
import { LinkTarget } from 'types';
import { IAppSection, IMenuItem } from '../types';
import { renderIcon } from '../utils';
import { checkAbsoluteRowIfExist } from '../../utils';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';

const SaasMenuItem = ({ saasItem, pl, parentRoute }: { saasItem: IMenuItem | IAppSection; pl?: number; parentRoute: string }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const modal = useConfirmationModalContext();
    const to = checkAbsoluteRowIfExist(saasItem.route) ? saasItem.route : `${parentRoute}/${saasItem.route}`;

    const { openedAction, openedForm } = useSelector((store) => store.menu);
    const { borderRadius } = useConfig();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const [selected, setSelected] = useState<string | boolean | null | undefined>(null);

    const onClickMenuItem = async (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (!openedForm && !openedAction) {
            itemHandler();
            return;
        }
        await modal.showConfirmation({
            title: 'Are you sure you want to discard your changes?',
            content: (
                <Alert
                    variant="filled"
                    icon={false}
                    sx={{
                        bgcolor: '#FB9000',
                        color: 'white',
                        borderRadius: '8px !important',
                        height: '32px',
                        fontSize: '14px',
                        width: 'fit-content',
                        padding: '0 16px !important',
                        fontWeight: 500
                    }}
                >
                    This is an irreversible action and will permanently remove your work.
                </Alert>
            ),
            forwardButtonText: 'Discard Changes',
            onAction: () => {},
            onForward: itemHandler,
            showCheckbox: false,
            actionType: 'change_menu_item',
            sectionTitle: 'record_grid',
            recordType: '',
            hideCloseBtn: true,
            showActionButton: false
        });
    };
    const itemHandler = async () => {
        const id = saasItem.id!;
        dispatch(cleanOpenedAction());
        navigate(to);
        dispatch(activeItem([id]));
        dispatch(activeMenuItem(saasItem));

        matchesSM && dispatch(openDrawer(false));
    };

    const listItemProps: {
        component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
        href?: string;
        target?: LinkTarget;
    } = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} onClick={onClickMenuItem} to={to} target="_self" />)
    };

    const menuIcon = saasItem.icon ? (
        renderIcon(saasItem.icon)
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: selected === saasItem.id ? 8 : 6,
                height: selected === saasItem.id ? 8 : 6
            }}
            fontSize="medium"
        />
    );

    useEffect(() => {
        const pathNameArr = pathname.split('/');
        const menuItemUrl = pathNameArr[pathNameArr.length - 1];
        setSelected(menuItemUrl === saasItem.route);
    }, [pathname, saasItem.route]);

    return (
        <ListItemButton
            {...listItemProps}
            sx={{
                borderRadius: `${borderRadius}px`,
                mb: 0.5,
                alignItems: 'flex-start',
                // backgroundColor: 'inherit',
                // py: level > 1 ? 1 : 1.25,
                pl: pl || 6
            }}
            selected={!!selected}
        >
            <ListItemIcon sx={{ my: 'auto', minWidth: saasItem.icon ? 36 : 18 }}>{menuIcon}</ListItemIcon>
            <ListItemText
                primary={
                    <Typography
                        variant={selected ? 'h5' : 'body1'}
                        color="inherit"
                        sx={{ maxWidth: '75%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {saasItem.name}
                    </Typography>
                }
            />
        </ListItemButton>
    );
};

export default SaasMenuItem;
